import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, Trash2, Plus, ArrowRight, Loader2, Home } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CORRIDORS } from "@/lib/constants";

const SUGGESTED_NAMES = [
  "Loyer maman", "Frais de scolarité", "Courses semaine", "Salaire ménagère",
  "Paiement fournisseur", "Aide famille", "Santé / médecin",
];

export default function Templates() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: "",
    corridor: "tg_ga",
    beneficiary_name: "",
    beneficiary_phone: "",
    beneficiary_email: "",
    beneficiary_account: "",
    suggested_amount: "",
    note: "",
  });
  const corridorDef = CORRIDORS[form.corridor];

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/me/templates");
        setItems(Array.isArray(data) ? data : []);
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [user, authLoading, navigate]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.beneficiary_name.trim()) {
      toast.error("Nom du modèle et nom du bénéficiaire requis.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        corridor: form.corridor,
        beneficiary_name: form.beneficiary_name.trim(),
        beneficiary_phone: form.beneficiary_phone.trim() || null,
        beneficiary_email: form.beneficiary_email.trim() || null,
        beneficiary_account: form.beneficiary_account.trim() || null,
        suggested_amount: form.suggested_amount ? Number(form.suggested_amount) : null,
        note: form.note.trim() || null,
      };
      const { data } = await api.post("/me/templates", payload);
      setItems((p) => [data, ...p]);
      setForm({ name: "", corridor: "tg_ga", beneficiary_name: "", beneficiary_phone: "", beneficiary_email: "", beneficiary_account: "", suggested_amount: "", note: "" });
      setShowForm(false);
      toast.success(`Modèle « ${data.name} » créé.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try {
      await api.delete(`/me/templates/${t.id}`);
      setItems((p) => p.filter((x) => x.id !== t.id));
      toast.success("Modèle supprimé.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const use = async (t) => {
    try { await api.post(`/me/templates/${t.id}/used`); } catch (_e) { /* ignore */ }
    const params = new URLSearchParams({ template: t.id });
    navigate(`/envoyer?${params.toString()}`);
  };

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const ua = a.last_used_at ? new Date(a.last_used_at).getTime() : 0;
      const ub = b.last_used_at ? new Date(b.last_used_at).getTime() : 0;
      if (ub !== ua) return ub - ua;
      return (b.use_count || 0) - (a.use_count || 0);
    });
  }, [items]);

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500">Envois récurrents</div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                <Bookmark className="text-[var(--sl-primary)]" /> Mes modèles
              </h1>
              <p className="text-gray-600 mt-1 text-sm">Enregistrez vos envois habituels (« Loyer maman », « Frais de scolarité »…) et démarrez un transfert en 1 clic.</p>
            </div>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="sl-btn-primary"
              data-testid="new-template-btn"
            >
              <Plus size={16} /> {showForm ? "Annuler" : "Nouveau modèle"}
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="mt-6 sl-card" data-testid="template-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sl-label">Nom du modèle *</label>
                  <input
                    className="sl-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Ex: Loyer maman"
                    data-testid="template-name"
                    required
                  />
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {SUGGESTED_NAMES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => update("name", s)}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--sl-bg-alt)] text-gray-600 hover:bg-[var(--sl-primary)]/10 hover:text-[var(--sl-primary)] transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="sl-label">Corridor *</label>
                  <select
                    className="sl-input"
                    value={form.corridor}
                    onChange={(e) => update("corridor", e.target.value)}
                    data-testid="template-corridor"
                  >
                    {Object.values(CORRIDORS).map((c) => (
                      <option key={c.id} value={c.id}>{c.flagFrom} {c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="sl-label">Nom du bénéficiaire *</label>
                  <input
                    className="sl-input"
                    value={form.beneficiary_name}
                    onChange={(e) => update("beneficiary_name", e.target.value)}
                    placeholder="Prénom Nom"
                    data-testid="template-benef-name"
                    required
                  />
                </div>
                <div>
                  <label className="sl-label">Téléphone bénéficiaire</label>
                  <input
                    className="sl-input"
                    value={form.beneficiary_phone}
                    onChange={(e) => update("beneficiary_phone", e.target.value)}
                    placeholder={`${corridorDef.beneficiaryDialCode} ...`}
                    data-testid="template-benef-phone"
                  />
                </div>
                {form.corridor === "ga_fr" && (
                  <>
                    <div>
                      <label className="sl-label">Email PayPal / Wero</label>
                      <input
                        className="sl-input"
                        value={form.beneficiary_email}
                        onChange={(e) => update("beneficiary_email", e.target.value)}
                        placeholder="exemple@email.com"
                        data-testid="template-benef-email"
                      />
                    </div>
                    <div>
                      <label className="sl-label">IBAN (virement instantané)</label>
                      <input
                        className="sl-input"
                        value={form.beneficiary_account}
                        onChange={(e) => update("beneficiary_account", e.target.value)}
                        placeholder="FRxx ..."
                        data-testid="template-benef-iban"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="sl-label">Montant suggéré ({corridorDef.source_currency})</label>
                  <input
                    type="number"
                    min="0"
                    className="sl-input"
                    value={form.suggested_amount}
                    onChange={(e) => update("suggested_amount", e.target.value)}
                    placeholder="Optionnel"
                    data-testid="template-amount"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="sl-label">Note interne</label>
                  <input
                    className="sl-input"
                    value={form.note}
                    onChange={(e) => update("note", e.target.value)}
                    placeholder="Optionnel — ex: à envoyer chaque 5 du mois"
                    data-testid="template-note"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <button type="submit" className="sl-btn-primary" disabled={busy} data-testid="template-submit">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                  Enregistrer le modèle
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-500 hover:underline">Annuler</button>
              </div>
            </form>
          )}

          <div className="mt-8 sl-card overflow-hidden p-0">
            {loading ? (
              <div className="p-10 text-center text-gray-500">Chargement…</div>
            ) : sorted.length === 0 ? (
              <div className="p-12 text-center">
                <Bookmark size={32} className="mx-auto text-gray-300" />
                <p className="text-gray-500 mt-3">Aucun modèle pour l&apos;instant.</p>
                <p className="text-xs text-gray-400 mt-1">Créez votre premier envoi récurrent pour démarrer un transfert en 1 clic.</p>
                <button onClick={() => setShowForm(true)} className="sl-btn-primary mt-5 inline-flex" data-testid="empty-create-template">
                  <Plus size={16} /> Créer un modèle
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {sorted.map((t) => {
                  const cdef = CORRIDORS[t.corridor] || CORRIDORS.tg_ga;
                  return (
                    <li key={t.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`template-row-${t.id}`}>
                      <div className="w-11 h-11 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center flex-shrink-0">
                        <Home size={20} className="text-[var(--sl-primary)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-base truncate">{t.name}</span>
                          <span className="text-[10px] uppercase tracking-widest text-gray-400">{cdef.short}</span>
                          {t.use_count > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                              utilisé {t.use_count}×
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 truncate">→ {t.beneficiary_name}{t.beneficiary_phone ? ` · ${t.beneficiary_phone}` : ""}</div>
                        {t.note && <div className="text-xs text-gray-500 mt-0.5 truncate">📝 {t.note}</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => use(t)}
                          className="sl-btn-primary !py-1.5 !px-4 !text-sm"
                          data-testid={`template-use-${t.id}`}
                        >
                          Démarrer <ArrowRight size={14} />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          className="w-9 h-9 rounded-full grid place-items-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                          title="Supprimer"
                          data-testid={`template-delete-${t.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="mt-6 text-center">
            <Link to="/mes-transactions" className="text-sm text-gray-500 hover:text-[var(--sl-primary)]">← Retour à mes envois</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
